import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowRight, MapPin, Mail, Clock, Phone, Calendar } from 'lucide-react';
import {
  BANDEAU,
  STRIP_CHIFFRES,
  EXPERIENCE,
  APERCUS,
  CARTE,
  VISITE,
  PROMESSE,
  BRAND,
} from '@/data/marketing/content';
import {
  VISITE_PHOTOS,
  COCKTAILS_HERO,
  VINS_HERO,
  DEGUSTATION_HERO,
  MENU_HERO,
} from '@/data/marketing/photos';
import { cn } from '@/lib/utils';
import { useDocumentMeta } from '@/lib/useDocumentMeta';
import NextEventHighlight from '@/features/marketing/NextEventHighlight';
import { useOpeningHours } from '@/features/marketing/useOpeningHours';
import {
  useIncrementPageVisit,
  usePageVisitCount,
} from '@/features/marketing/usePageVisits';
import { useTopRatedCocktails } from '@/features/customers/useTopRated';
import { getCocktailPhotoPublicUrl } from '@/features/cocktails/photoStorage';
import { StarRating } from '@/components/StarRating';
import HeroImmersive from '@/features/marketing/HeroImmersive';
import AnimatedSection from '@/components/AnimatedSection';
import ParallaxImage from '@/components/ParallaxImage';
import { FOCAL } from '@/lib/imageSlots';
import CountUp from '@/components/CountUp';
import { fadeUp, slideLeft, slideRight, scaleIn } from '@/lib/animations';

export default function Home() {
  useDocumentMeta({
    title: "Bar L'Alambic — Bar de dégustation à La Sarre, Abitibi",
    description:
      "Bar de dégustation au cœur de La Sarre. 60+ whiskys, 8 microbrasseries québécoises, 20 vins d'importation privée et cocktails travaillés. Plateaux de dégustation, tartares et fromages d'ici.",
  });

  useIncrementPageVisit('/');

  return (
    <>
      <HeroImmersive />
      <NextEventHighlight />
      <BandeauNouvelleBouteille />
      <StripChiffres />
      <Experience />
      <ApercusPhotos />
      <Carte />
      <MidScrollCTA />
      <Visite />
      <Promesse />
    </>
  );
}

function BandeauNouvelleBouteille() {
  return (
    <AnimatedSection
      as="section"
      variant="fadeIn"
      className="relative border-y border-[#1F1F1F] bg-[#0F0F0F] overflow-hidden"
    >
      <div className="max-w-7xl mx-auto px-5 lg:px-8 py-8 flex items-center justify-center">
        <div className="flex items-center gap-4">
          <span className="hidden md:inline-block h-px w-12 bg-[#D4A574]/40" />
          <p className="font-display italic text-lg md:text-2xl text-[#D4A574] text-center">
            {BANDEAU.text}
          </p>
          <span className="hidden md:inline-block h-px w-12 bg-[#D4A574]/40" />
        </div>
      </div>
    </AnimatedSection>
  );
}

function parseChiffre(raw: string): { prefix: string; to: number; suffix: string } {
  const match = raw.match(/^(\D*)(\d+(?:[.,]\d+)?)(\D*)$/);
  if (!match) return { prefix: '', to: 0, suffix: raw };
  const [, prefix, num, suffix] = match;
  return { prefix, to: Number(num.replace(',', '.')), suffix };
}

function StripChiffres() {
  const { data: visitCount = 0 } = usePageVisitCount('/');

  return (
    <AnimatedSection
      as="section"
      stagger={0.1}
      className="border-b border-[#1F1F1F]"
    >
      <div className="max-w-7xl mx-auto px-5 lg:px-8 py-14 grid grid-cols-2 md:grid-cols-5 gap-6 md:gap-8">
        {STRIP_CHIFFRES.map((c, i) => {
          const { prefix, to, suffix } = parseChiffre(c.value);
          return (
            <motion.div
              key={c.label}
              variants={fadeUp}
              className={cn(
                'text-center md:text-left',
                i > 0 && 'md:border-l md:border-[#1F1F1F] md:pl-8'
              )}
            >
              <CountUp
                to={to}
                prefix={prefix}
                suffix={suffix}
                duration={1.8}
                className="block font-display text-4xl md:text-5xl text-[#D4A574] tabular-nums leading-none"
              />
              <div className="mt-2 text-[11px] uppercase tracking-[0.2em] text-neutral-300">
                {c.label}
              </div>
            </motion.div>
          );
        })}

        <motion.div
          variants={fadeUp}
          className="text-center md:text-left md:border-l md:border-[#1F1F1F] md:pl-8"
        >
          <CountUp
            to={visitCount}
            duration={1.8}
            className="block font-display text-4xl md:text-5xl text-[#D4A574] tabular-nums leading-none"
          />
          <div className="mt-2 text-[11px] uppercase tracking-[0.2em] text-neutral-300">
            Visites
          </div>
        </motion.div>
      </div>
    </AnimatedSection>
  );
}

function Experience() {
  return (
    <section className="border-b border-[#1F1F1F]">
      <div className="max-w-7xl mx-auto px-5 lg:px-8 py-24 grid gap-12 lg:gap-20 lg:grid-cols-[1fr_1.2fr]">
        <motion.div
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, amount: 0.3 }}
          variants={slideLeft}
        >
          <Eyebrow>{EXPERIENCE.eyebrow}</Eyebrow>
          <h2 className="font-display text-3xl md:text-5xl mt-4 leading-tight text-[#E8E0D6]">
            {EXPERIENCE.title}
          </h2>
          <p className="mt-6 text-neutral-300 leading-relaxed">
            {EXPERIENCE.description}
          </p>
        </motion.div>

        <AnimatedSection as="div" stagger={0.15}>
          <ul className="space-y-6">
            {EXPERIENCE.values.map((v, i) => (
              <motion.li
                key={v.title}
                variants={slideRight}
                className="group flex items-start gap-5 pb-6 border-b border-[#1F1F1F] last:border-b-0 last:pb-0"
              >
                <span className="font-display text-2xl text-[#D4A574]/70 tabular-nums w-10 shrink-0">
                  {String(i + 1).padStart(2, '0')}
                </span>
                <div>
                  <h3 className="font-display italic text-2xl text-[#D4A574]">
                    {v.title}
                  </h3>
                  <p className="mt-2 text-sm text-neutral-300 leading-relaxed">
                    {v.description}
                  </p>
                </div>
              </motion.li>
            ))}
          </ul>
        </AnimatedSection>
      </div>
    </section>
  );
}

function ApercusPhotos() {
  const { data: top = [] } = useTopRatedCocktails(3);

  const items =
    top.length > 0
      ? top.map(({ cocktail, rating_avg, rating_count }) => ({
          id: cocktail.id,
          name: cocktail.name,
          image:
            getCocktailPhotoPublicUrl(cocktail.photo_path, {
              width: 600,
              quality: 75,
            }) ?? '/images/marketing/whisky-sour.webp',
          desc: cocktail.category?.label ?? 'Cocktail signature',
          rating_avg,
          rating_count,
        }))
      : APERCUS.items.map((c) => ({
          id: c.name,
          name: c.name,
          image: c.image,
          desc: c.desc,
          rating_avg: null as number | null,
          rating_count: 0,
        }));

  return (
    <section className="border-b border-[#1F1F1F]">
      <div className="max-w-7xl mx-auto px-5 lg:px-8 py-24">
        <AnimatedSection as="div" className="text-center max-w-2xl mx-auto mb-14">
          <Eyebrow center>{APERCUS.eyebrow}</Eyebrow>
          <h2 className="font-display text-3xl md:text-5xl mt-4 leading-tight text-[#E8E0D6]">
            {APERCUS.title}
          </h2>
        </AnimatedSection>

        <AnimatedSection as="div" stagger={0.15} className="grid gap-6 md:grid-cols-3">
          {items.map((c, idx) => {
            const href =
              top.length > 0 ? `/cocktails?id=${c.id}` : '/cocktails';
            return (
              <motion.div key={c.id} variants={fadeUp}>
                <Link
                  to={href}
                  className="group relative block rounded-2xl overflow-hidden border border-[#1F1F1F] bg-[#0F0F0F] hover:border-[#D4A574]/40 transition-all"
                >
                  <div className="aspect-[4/5] relative overflow-hidden bg-[#0A0A0A]">
                    <ParallaxImage
                      src={c.image}
                      alt={c.name}
                      amplitude={25}
                      objectPosition={FOCAL.people}
                      className="absolute inset-0 h-full w-full"
                      imgClassName="transition-transform duration-700 group-hover:scale-105"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-[#0F0F0F] via-transparent to-transparent pointer-events-none" />
                    {top.length > 0 && (
                      <div className="absolute top-3 left-3 h-9 w-9 rounded-full bg-[#D4A574] text-[#0A0A0A] flex items-center justify-center font-display text-lg font-bold shadow-lg">
                        {idx + 1}
                      </div>
                    )}
                  </div>
                  <div className="p-6">
                    <h3 className="font-display text-xl text-[#E8E0D6] group-hover:text-[#D4A574] transition-colors">
                      {c.name}
                    </h3>
                    <p className="mt-2 text-sm text-neutral-300 leading-relaxed">
                      {c.desc}
                    </p>
                    {c.rating_avg !== null && (
                      <div className="mt-3">
                        <StarRating
                          value={c.rating_avg}
                          readOnly
                          size="sm"
                          label={`${c.rating_avg.toFixed(1)} · ${c.rating_count} avis`}
                        />
                      </div>
                    )}
                  </div>
                </Link>
              </motion.div>
            );
          })}
        </AnimatedSection>
      </div>
    </section>
  );
}

const PORTAL_PHOTOS = [
  COCKTAILS_HERO,
  VINS_HERO,
  DEGUSTATION_HERO,
  MENU_HERO,
] as const;

function Carte() {
  return (
    <section className="border-b border-[#1F1F1F] bg-[#0C0C0C]">
      <div className="max-w-7xl mx-auto px-5 lg:px-8 py-24">
        <AnimatedSection as="div" className="text-center max-w-2xl mx-auto mb-14">
          <Eyebrow center>{CARTE.eyebrow}</Eyebrow>
          <h2 className="font-display text-3xl md:text-5xl mt-4 leading-tight text-[#E8E0D6]">
            {CARTE.title}
          </h2>
          <p className="mt-6 text-neutral-300 leading-relaxed">
            {CARTE.description}
          </p>
        </AnimatedSection>

        <AnimatedSection
          as="div"
          stagger={0.12}
          className="grid gap-5 md:grid-cols-2"
        >
          {CARTE.portals.map((p, i) => (
            <motion.div key={p.href} variants={fadeUp}>
              <Link
                to={p.href}
                className="group relative block overflow-hidden rounded-2xl border border-[#1F1F1F] hover:border-[#D4A574]/40 transition-colors duration-500 h-[340px] md:h-[380px]"
              >
                <motion.img
                  src={PORTAL_PHOTOS[i]}
                  alt=""
                  loading={i < 2 ? 'eager' : 'lazy'}
                  fetchPriority={i < 2 ? 'high' : 'auto'}
                  whileHover={{ scale: 1.08 }}
                  transition={{ duration: 1.4, ease: 'easeOut' }}
                  style={{ objectPosition: FOCAL.people }}
                  className="absolute inset-0 h-full w-full object-cover"
                />

                <div className="absolute inset-0 bg-gradient-to-t from-[#0A0A0A]/95 via-[#0A0A0A]/55 to-[#0A0A0A]/30" />
                <div className="absolute inset-0 bg-gradient-to-r from-[#0A0A0A]/40 via-transparent to-transparent" />

                <div className="pointer-events-none absolute inset-0 ring-1 ring-inset ring-transparent group-hover:ring-[#D4A574]/40 transition-all duration-500" />
                <div className="pointer-events-none absolute -bottom-32 -right-20 h-[280px] w-[280px] rounded-full bg-[#D4A574]/0 group-hover:bg-[#D4A574]/8 blur-3xl transition-colors duration-700" />

                <div className="relative z-10 h-full flex flex-col justify-end p-7 md:p-9">
                  <div className="inline-flex items-center gap-2 text-[10px] uppercase tracking-[0.3em] text-[#D4A574]">
                    <span className="h-px w-6 bg-[#D4A574]/40" />
                    {p.kicker}
                  </div>
                  <h3 className="font-display text-3xl md:text-4xl text-[#E8E0D6] mt-3 leading-tight drop-shadow-[0_2px_8px_rgba(0,0,0,0.6)] group-hover:text-[#D4A574] transition-colors">
                    {p.title}
                  </h3>
                  <p className="mt-3 text-sm text-neutral-200 leading-relaxed max-w-md drop-shadow-[0_1px_3px_rgba(0,0,0,0.6)]">
                    {p.desc}
                  </p>
                  <div className="mt-5 inline-flex items-center gap-2 text-sm font-medium text-[#D4A574] group-hover:gap-3 transition-all duration-300">
                    {p.cta}
                    <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
                  </div>
                </div>
              </Link>
            </motion.div>
          ))}
        </AnimatedSection>
      </div>
    </section>
  );
}

// CTA inline pour le client convaincu après les 4 portails Carte.
// Évite que le client doive scroller jusqu'en bas pour réserver.
function MidScrollCTA() {
  return (
    <section className="border-b border-[#1F1F1F] bg-[#0A0A0A]">
      <div className="max-w-7xl mx-auto px-5 lg:px-8 py-14">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.4 }}
          transition={{ duration: 0.5 }}
          className="flex flex-col md:flex-row items-center justify-between gap-6 rounded-2xl border border-[#D4A574]/30 bg-gradient-to-r from-[#D4A574]/5 via-transparent to-[#D4A574]/5 p-8 md:p-10"
        >
          <div className="text-center md:text-left">
            <h3 className="font-display text-2xl md:text-3xl text-[#E8E0D6] leading-tight">
              Une envie de venir ?
            </h3>
            <p className="mt-2 text-sm text-neutral-300">
              Réserve ta table en 30 secondes. Confirmation par courriel.
            </p>
          </div>
          <Link
            to="/reserver"
            className="group inline-flex items-center gap-2 px-6 py-3 rounded-md bg-[#D4A574] text-[#0A0A0A] font-medium text-sm tracking-wide hover:bg-[#C9A36B] transition-all shadow-lg shadow-[#D4A574]/20 whitespace-nowrap"
          >
            <Calendar className="h-4 w-4" />
            Réserver une table
            <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5" />
          </Link>
        </motion.div>
      </div>
    </section>
  );
}

function Visite() {
  const { data: hours = VISITE.hours } = useOpeningHours();
  // Adresse cliquable : ouvre Google Maps avec une requête sur l'adresse complète.
  const mapsUrl = `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(VISITE.address)}`;

  return (
    <section id="visite" className="border-b border-[#1F1F1F]">
      <div className="max-w-7xl mx-auto px-5 lg:px-8 py-24 grid gap-12 lg:grid-cols-[1fr_1.1fr] items-start">
        <motion.div
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, amount: 0.2 }}
          variants={slideLeft}
        >
          <Eyebrow>{VISITE.eyebrow}</Eyebrow>
          <h2 className="font-display text-3xl md:text-5xl mt-4 leading-tight text-[#E8E0D6]">
            {VISITE.title}
          </h2>

          <div className="mt-8 space-y-4">
            <div className="flex items-start gap-3">
              <MapPin className="h-4 w-4 text-[#D4A574] mt-0.5 shrink-0" />
              <div>
                <div className="text-[10px] uppercase tracking-[0.2em] text-neutral-400">
                  Adresse
                </div>
                <a
                  href={mapsUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="mt-1 inline-block text-[#E8E0D6] hover:text-[#D4A574] transition-colors underline decoration-[#D4A574]/30 underline-offset-2 hover:decoration-[#D4A574]"
                >
                  {VISITE.address}
                </a>
              </div>
            </div>
            <div className="flex items-start gap-3">
              <Phone className="h-4 w-4 text-[#D4A574] mt-0.5 shrink-0" />
              <div>
                <div className="text-[10px] uppercase tracking-[0.2em] text-neutral-400">
                  Téléphone
                </div>
                <a
                  href={`tel:${VISITE.phone.replace(/[^0-9+]/g, '')}`}
                  className="mt-1 inline-block text-[#E8E0D6] hover:text-[#D4A574] transition-colors tabular-nums"
                >
                  {VISITE.phone}
                </a>
              </div>
            </div>
            <div className="flex items-start gap-3">
              <Mail className="h-4 w-4 text-[#D4A574] mt-0.5 shrink-0" />
              <div>
                <div className="text-[10px] uppercase tracking-[0.2em] text-neutral-400">
                  Courriel
                </div>
                <a
                  href={`mailto:${VISITE.email}`}
                  className="mt-1 inline-block text-[#E8E0D6] hover:text-[#D4A574] transition-colors"
                >
                  {VISITE.email}
                </a>
              </div>
            </div>
          </div>

          <div className="mt-10 rounded-2xl border border-[#1F1F1F] bg-[#0F0F0F] p-6">
            <div className="flex items-center gap-2 mb-4">
              <Clock className="h-4 w-4 text-[#D4A574]" />
              <span className="text-[10px] uppercase tracking-[0.2em] text-neutral-300">
                Horaires
              </span>
            </div>
            <ul className="divide-y divide-[#1F1F1F]">
              {hours.map((h) => (
                <li
                  key={h.day}
                  className="flex items-center justify-between py-3 first:pt-0 last:pb-0"
                >
                  <span className="text-sm text-[#E8E0D6]">{h.day}</span>
                  <span className="text-sm text-neutral-300 tabular-nums">
                    {h.hours}
                  </span>
                </li>
              ))}
            </ul>
          </div>

          <p className="mt-6 text-xs text-neutral-400 italic max-w-md">
            {VISITE.note}
          </p>
        </motion.div>

        <motion.div
          className="grid grid-cols-2 gap-4 md:gap-5 relative"
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, amount: 0.2 }}
          variants={{
            hidden: {},
            visible: { transition: { staggerChildren: 0.12, delayChildren: 0.2 } },
          }}
        >
          <div className="pointer-events-none absolute -top-16 -right-16 h-64 w-64 rounded-full bg-[#D4A574]/5 blur-3xl" />
          <div className="pointer-events-none absolute -bottom-16 -left-16 h-64 w-64 rounded-full bg-[#D4A574]/5 blur-3xl" />

          {VISITE_PHOTOS.map((photo, i) => (
            <motion.div
              key={photo.src}
              variants={fadeUp}
              whileHover={{ y: -4, transition: { duration: 0.3 } }}
              className={cn(
                'group relative aspect-[4/5] rounded-2xl overflow-hidden border border-[#1F1F1F]',
                'hover:border-[#D4A574]/40 transition-colors duration-500',
                'shadow-xl hover:shadow-2xl hover:shadow-[#D4A574]/10',
                i === 0 && 'mt-0',
                i === 1 && 'mt-16',
                i === 2 && '-mt-8',
                i === 3 && 'mt-8'
              )}
            >
              <ParallaxImage
                src={photo.src}
                alt={photo.alt}
                amplitude={35 + i * 10}
                objectPosition={FOCAL.people}
                className="h-full w-full"
                imgClassName="transition-transform duration-700 group-hover:scale-110"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-[#0A0A0A]/40 via-transparent to-transparent pointer-events-none" />
              <div className="absolute inset-0 rounded-2xl ring-1 ring-inset ring-transparent group-hover:ring-[#D4A574]/30 transition-all duration-500 pointer-events-none" />
            </motion.div>
          ))}
        </motion.div>
      </div>
    </section>
  );
}

function Promesse() {
  return (
    <section className="relative overflow-hidden">
      <div className="pointer-events-none absolute -top-32 left-1/2 -translate-x-1/2 h-[500px] w-[500px] rounded-full border border-[#D4A574]/10" />
      <div className="pointer-events-none absolute top-10 left-1/2 -translate-x-1/2 h-[300px] w-[300px] rounded-full border border-[#D4A574]/20" />

      <AnimatedSection
        as="div"
        stagger={0.12}
        className="relative max-w-4xl mx-auto px-5 lg:px-8 py-32 text-center"
      >
        <motion.div variants={fadeUp}>
          <Eyebrow center>{PROMESSE.eyebrow}</Eyebrow>
        </motion.div>
        <motion.h2
          variants={fadeUp}
          className="font-display italic text-4xl md:text-6xl mt-6 leading-[1.1] text-[#D4A574]"
        >
          {PROMESSE.title}
        </motion.h2>
        <motion.p
          variants={fadeUp}
          className="mt-8 text-base md:text-lg text-neutral-300 leading-relaxed max-w-2xl mx-auto"
        >
          {PROMESSE.description}
        </motion.p>

        <motion.div variants={scaleIn} className="mt-10">
          <Link
            to={PROMESSE.cta.href}
            className="group inline-flex items-center gap-2 px-7 py-3.5 rounded-md bg-[#D4A574] text-[#0A0A0A] font-medium text-sm tracking-wide hover:bg-[#C9A36B] transition-all shadow-2xl shadow-[#D4A574]/20"
          >
            {PROMESSE.cta.label}
            <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5" />
          </Link>
        </motion.div>

        <motion.p
          variants={fadeUp}
          className="mt-12 text-[11px] uppercase tracking-[0.3em] text-neutral-400"
        >
          {BRAND.name} — fondé en {BRAND.foundedYear}
        </motion.p>
      </AnimatedSection>
    </section>
  );
}

function Eyebrow({
  children,
  center = false,
}: {
  children: React.ReactNode;
  center?: boolean;
}) {
  return (
    <div
      className={cn(
        'inline-flex items-center gap-2 text-[10px] uppercase tracking-[0.25em] text-[#D4A574]/80',
        center && 'justify-center'
      )}
    >
      <span className="h-px w-6 bg-[#D4A574]/40" />
      {children}
    </div>
  );
}
